import React, { Suspense, useEffect, useLayoutEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import {
  AuthProvider,
  useAuthContext,
} from "./components/context/useAuthContext";
import { ThemeProvider } from "./components/context";
import ErrorBoundary from "./components/ErrorBoundary";
import { getAppRoutes } from "./components/AppRoute";
import BasicDetail from "./components/pages/BasicDetail";
import { SuspenseFallback } from "./components/SuspenseFallback";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsConditions from "./components/pages/TermsConditions";
import RefundPolicy from "./components/pages/RefundPolicy";
import HelpBot from "./components/common/HelpBot";

// Lazy imports
const Home = React.lazy(() => import("./components/pages/Home"));
const Hometwo = React.lazy(() => import("./components/pages/Hometwo"));
const Blog = React.lazy(() => import("./components/pages/Blog"));
const Blogstandard = React.lazy(() =>
  import("./components/pages/Blogstandard")
);
const Blogdetails = React.lazy(() => import("./components/pages/Blogdetails"));
const About = React.lazy(() => import("./components/pages/About"));
const MyProfile = React.lazy(() => import("./components/pages/MyProfile"));
const DoctorProfile = React.lazy(() =>
  import("./components/pages/DoctorProfile")
);
const Services = React.lazy(() => import("./components/pages/Services"));
const Servicedetails = React.lazy(() =>
  import("./components/pages/Servicedetails")
);
const Faqs = React.lazy(() => import("./components/pages/Faqs"));
const Appointment = React.lazy(() => import("./components/pages/Appointment"));
const Clinicgrid = React.lazy(() => import("./components/pages/Clinicgrid"));
const Cliniclist = React.lazy(() => import("./components/pages/Cliniclist"));
const Clinicdetails = React.lazy(() =>
  import("./components/pages/Clinicdetails")
);
const ClinicListdetails = React.lazy(() =>
  import("./components/pages/ClinicListdetails")
);
const Doctorgrid = React.lazy(() => import("./components/pages/Doctorgrid"));
const Doctorlist = React.lazy(() => import("./components/pages/Doctorlist"));
const Doctordetails = React.lazy(() =>
  import("./components/pages/Doctordetails")
);
const Contact = React.lazy(() => import("./components/pages/Contact"));
const Errorpage = React.lazy(() => import("./components/pages/Errorpage"));
const HospitalList = React.lazy(() => import("./components/pages/HospitalList"));
const UserLogin = React.lazy(() => import("./components/pages/UserLogin"));
const AppRedirect = React.lazy(() => import("./components/pages/AppRedirect"));

// Scroll to top on route change
function ScrollToTop({ children }) {
  const location = useLocation();

  useLayoutEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const hashId = decodeURIComponent(location.hash.replace(/^#/, ""));
    const scrollToHash = () => {
      const target = document.getElementById(hashId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timeoutId = window.setTimeout(scrollToHash, 60);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return;
    }

    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  return children || null;
}

// This component is inside AuthProvider, so hooks work fine
function AppRoutes() {
  const auth = useAuthContext();

  return (
    <Routes>
      {/* Public and static routes */}
      <Route path="/" element={<Home />} />
      <Route path="/home-v2" element={<Hometwo />} />

      <Route path="/blog" element={<Blog />} />
      <Route path="/blog-standard" element={<Blogstandard />} />
      <Route
        path="/blog-details/:id"
        element={<Blogdetails key={window.location.pathname} />}
      />
      <Route
        path="/blog/cat/:catId"
        element={<Blog key={window.location.pathname} />}
      />
      <Route
        path="/blog/tag/:tagId"
        element={<Blog key={window.location.pathname} />}
      />
      <Route
        path="/blog/search/:query"
        element={<Blog key={window.location.pathname} />}
      />
      <Route
        path="/blog/author/:authorId"
        element={<Blog key={window.location.pathname} />}
      />

      <Route path="/myprofile" element={<MyProfile />} />
      <Route path="/doctorprofile" element={<DoctorProfile />} />
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route
        path="/service/cat/:catId"
        element={<Services key={window.location.pathname} />}
      />
      <Route
        path="/service-details/:id"
        element={<Servicedetails key={window.location.pathname} />}
      />

      <Route path="/faqs" element={<Faqs />} />
      <Route path="/appointment" element={<Appointment />} />

      <Route path="/clinic-grid" element={<Clinicgrid />} />
      <Route path="/clinic-list" element={<Cliniclist />} />
      <Route
        path="/clinic/cat/:catId"
        element={<Clinicgrid key={window.location.pathname} />}
      />
      <Route
        path="/clinic-details/:id"
        element={<Clinicdetails key={window.location.pathname} />}
      />
      <Route
        path="/clinic-list-details"
        element={<ClinicListdetails key={window.location.pathname} />}
      />

      <Route path="/doctor-grid" element={<Doctorgrid />} />
      <Route path="/doctor-list" element={<Doctorlist />} />
      <Route
        path="/doctor/cat/:catId"
        element={<Doctorgrid key={window.location.pathname} />}
      />
      <Route
        path="/doctor-details"
        element={<Doctordetails key={window.location.pathname} />}
      />

      <Route path="/contact" element={<Contact />} />
      <Route path="/basic-details" element={<BasicDetail />} />
      <Route path="/userlogin" element={<UserLogin />} />
      <Route path="/hospital-list" element={<HospitalList />} />
      <Route path="/app-redirect" element={<AppRedirect />} />

      {/* Policy pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />

      {/* Dynamically generated routes based on auth */}
      {getAppRoutes(auth)}

      {/* Fallback */}
      <Route path="*" element={<Errorpage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <HelmetProvider>
        <ErrorBoundary>
          <Router>
            <AuthProvider>
              <Suspense
                fallback={
                  <SuspenseFallback
                    text="Loading..."
                    variant="success"
                    centered={true}
                  />
                }
              >
                <ScrollToTop>
                  <>
                    <AppRoutes />
                    <HelpBot />
                  </>
                </ScrollToTop>
              </Suspense>
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
